import {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { TypedFilter } from "odata-query";
import { DEFAULT_PAGE_SIZE, HTTP_STATUSES } from "@miplatform/constants";
import { useBoolean, useDebounce } from "@miplatform/hooks";
import { IApiRequest } from "@miplatform/types";
import { buildSearchQuery } from "@miplatform/utils";
import {
  Autocomplete,
  Box,
  CircularProgress,
  MenuItem,
  SxProps,
  TextField,
} from "@mui/material";

type AllowedKeys<T> = keyof T & string;

export interface ServerAutocompleteProps<T> {
  allOption?: boolean;
  label?: string;
  name?: string;
  value?: T | any; // Can be primitive valueKey or T[] if multiple
  top?: number;
  options?: T[];
  labelKey?: AllowedKeys<T>;
  valueKey?: AllowedKeys<T>;
  filterKeys: AllowedKeys<T>[];
  multiple?: boolean;
  freeSolo?: boolean;
  clearOnBlur?: boolean;
  error?: { message?: string } | boolean;
  filters?: TypedFilter<T>;
  helperText?: string;
  limitTags?: number;
  required?: boolean;
  sx?: SxProps;
  "data-testId"?: string;
  dataTestId?: string;
  getOptionLabel?: (option: T) => string;
  renderOption?: (option: T) => ReactNode;
  onChange: (value: any) => void;
  getter: (request: IApiRequest<T>) => Promise<T[] | { data: T[] } | any>;
}

export const ServerAutocomplete = <T extends Record<string, any>>({
  allOption,
  label,
  value,
  top = DEFAULT_PAGE_SIZE,
  onChange,
  getter,
  labelKey,
  valueKey = "id" as AllowedKeys<T>,
  filterKeys,
  getOptionLabel,
  renderOption,
  multiple = false,
  freeSolo = false,
  clearOnBlur = false,
  error,
  helperText,
  limitTags,
  required,
  sx,
  ...props
}: ServerAutocompleteProps<T>) => {
  const initialValueRef = useRef(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [options, setOptions] = useState<T[]>([]);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, turnOnLoader, turnOffLoader] = useBoolean();
  const [isFocused, turnOnFocus, turnOffFocus] = useBoolean();
  const debouncedSearch = useDebounce(inputValue, 300);
  const updatedOptions = useMemo(() => {
    const updatedOptions = props.options || options;

    return allOption
      ? [{ [labelKey || "label"]: "All", [valueKey]: "All" }, ...updatedOptions]
      : updatedOptions;
  }, [options, props.options, allOption, labelKey, valueKey]);

  const selectedOption = useMemo(() => {
    if (multiple) return value || [];

    const selectedValue = updatedOptions.find(
      (item) => item?.[valueKey] === value || item === value
    );

    return selectedValue;
  }, [updatedOptions, isFocused, value, valueKey, multiple]);

  const selectedValue = useMemo(() => {
    if (isFocused) return null;

    return selectedOption;
  }, [isFocused, selectedOption]);

  const fetchOptions = useCallback(
    async (searchQuery: string) => {
      turnOnLoader();

      const query = buildSearchQuery(searchQuery, filterKeys);
      const response = await getter({
        top,
        filters: { ...props.filters, ...query } as TypedFilter<T>,
        onFail: turnOffLoader,
        onSuccess: turnOffLoader,
      });

      if (response.status === HTTP_STATUSES.OK && !props.options) {
        setOptions(response.data);
      }

      turnOffLoader();
    },
    [filterKeys, top]
  );

  useEffect(() => {
    fetchOptions(debouncedSearch);
  }, [debouncedSearch]);

  const handleGetOptionLabel = (option: T | string): string => {
    if (typeof option === "string") return option;
    if (getOptionLabel) return getOptionLabel(option);
    if (labelKey) return String(option?.[labelKey] || "");

    return String(option);
  };

  useEffect(() => {
    if (initialValueRef.current === null && selectedValue) {
      initialValueRef.current = value;
      setRefreshKey((prev) => prev + 1);
    }
  }, [selectedValue]);

  return (
    <Autocomplete
      key={refreshKey}
      loading={isLoading}
      multiple={multiple}
      freeSolo={freeSolo}
      clearOnBlur={clearOnBlur}
      limitTags={limitTags}
      data-testId={props["data-testId"] || props.dataTestId}
      options={updatedOptions as T[]}
      value={selectedValue}
      sx={sx}
      filterOptions={(o) => o}
      getOptionLabel={handleGetOptionLabel}
      getOptionKey={(option) =>
        typeof option === "string" ? option : option[valueKey]
      }
      isOptionEqualToValue={(option, val) =>
        typeof option === "string"
          ? option === val
          : option[valueKey] ===
            (typeof val === "string" ? val : val?.[valueKey])
      }
      onInputChange={(_e, newInputValue) => {
        setInputValue(newInputValue);
        if (newInputValue === "") {
          onChange(multiple ? [] : "");
        }
      }}
      onChange={(_e, newValue) => {
        if (multiple) {
          onChange(newValue);
        } else if (
          newValue &&
          typeof newValue === "object" &&
          !Array.isArray(newValue)
        ) {
          onChange((newValue as T)[valueKey]);
        } else if (typeof newValue === "string") {
          onChange(newValue);
        }
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          required={required}
          size="small"
          label={label}
          placeholder={multiple ? label : ""}
          error={!!error}
          helperText={typeof error === "object" ? error?.message : helperText}
          slotProps={{
            input: {
              ...params.InputProps,
              onFocus: turnOnFocus,
              onBlur: turnOffFocus,
              endAdornment: (
                <>
                  {isLoading ? (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        position: "absolute",
                        right: 36,
                        backgroundColor: "transparent",
                      }}
                    >
                      <CircularProgress color="inherit" size={20} />
                    </Box>
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            },
            htmlInput: {
              ...params.inputProps,
              "data-testid": `server-autocomplete-input-${props.name}`,
            },
          }}
          onKeyDown={(e) => {
            if (
              e.key === "Backspace" &&
              !params.inputProps.value &&
              !multiple
            ) {
              onChange("");
            }
          }}
        />
      )}
      renderOption={(props, option) => {
        const { key, ...optionProps } = props;
        const isSelected =
          typeof option === "string"
            ? option === selectedOption
            : option[valueKey] === selectedOption?.[valueKey];

        return (
          <MenuItem
            {...optionProps}
            selected={isSelected}
            key={typeof option === "string" ? option : option[valueKey]}
          >
            {renderOption
              ? renderOption(option as T)
              : handleGetOptionLabel(option)}
          </MenuItem>
        );
      }}
    />
  );
};
